import Dropdown from "bootstrap/js/dist/dropdown";
import qs from "qs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { postLogout } from "services/api/taptype/auth";
import { actions as authActions, userSelector } from "store/auth";
import { globalsSelector } from "store/globals";
import { segmentsSelector } from "store/segments";
import { apiHandler, getPharmacySegmentFromSegments, getRouteLocale } from "utils/helpers";
import vars from "utils/vars";
import LocaleLink from "views/components/LocaleLink";

const HeaderUserDropdown = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { i18n, t } = useTranslation();

	// redux global state
	const user = useSelector(userSelector);
	const globals = useSelector(globalsSelector);
	const segments = useSelector(segmentsSelector);

	// ref hook
	const logoutCancelRequestRef = useRef(null);
	const dropdownRef = useRef(null);

	// memo hook
	const pharmacySegment = useMemo(() => getPharmacySegmentFromSegments(segments), [segments]);
	const userFullName =
		user?.full_name || [user?.first_name || "", user?.last_name || ""].filter(Boolean).join(" ").trim() || "";

	// state hooks
	const [isLogoutUserLoadingState, setIsLogoutUserLoadingState] = useState(false);

	// event handlers
	const onLogoutButtonClickHandler = async () => {
		setIsLogoutUserLoadingState(true);

		if (logoutCancelRequestRef.current?.signal) logoutCancelRequestRef.current?.abort();
		logoutCancelRequestRef.current = new AbortController();

		await apiHandler(postLogout, {
			data: { body: { email: user?.email }, options: { signal: logoutCancelRequestRef.current.signal } },
			onError: () => setIsLogoutUserLoadingState(false),
			onSuccess: () => {
				setIsLogoutUserLoadingState(false);
				setTimeout(() => {
					navigate(
						`${getRouteLocale(i18n.language)}/auth/login?${qs.stringify({
							...(pharmacySegment?.id === globals?.segment?.id
								? { segment_id: pharmacySegment?.id }
								: {}),
						})}`
					);
				}, 0);
				dispatch(authActions.logout());
			},
		});
	};

	// effect hook
	useEffect(() => {
		const dropdownRefCurrent = dropdownRef?.current;
		let dropdown = Dropdown.getInstance(dropdownRefCurrent);
		if (!dropdown) dropdown = new Dropdown(dropdownRefCurrent);
		return () => (dropdown ? Dropdown.getInstance(dropdownRefCurrent).dispose() : false);
	}, []);

	useEffect(() => {
		return () => {
			if (logoutCancelRequestRef.current?.signal) logoutCancelRequestRef.current?.abort();
		};
	}, []);

	return (
		<div
			className="root-header-user-dropdown bg-primary text-white shadow-sm h-100"
			data-testid="rootHeaderUserDropdown">
			<div className="dropdown h-100">
				<button
					className="dropdown-toggle dropdown-toggle-with-rotate-icon btn btn-link text-reset text-decoration-none w-100 p-2 p-sm-3 px-md-4 rounded-0 border-0 h-100"
					type="button"
					title={userFullName}
					id="root-header-user-dropdown-toggle"
					data-bs-toggle="dropdown"
					aria-expanded="false"
					ref={dropdownRef}
					data-testid="rootHeaderUserDropdownToggleButton">
					<span className="d-flex align-items-center flex-nowrap">
						<span className="btn-text text-capitalize small text-start">
							<small className="small d-block mb-n1">{user?.job_title}</small>
							<span className="text-nowrap text-truncate d-block w-100">{userFullName}</span>
						</span>
						<span className="btn-icon ms-sm-2 ms-1">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="1em"
								height="1em"
								fill="currentColor"
								viewBox="0 0 16 16">
								<path
									fillRule="evenodd"
									d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
								/>
							</svg>
						</span>
					</span>
				</button>
				<ul
					className="dropdown-menu dropdown-menu-end w-fit-content mt-1 rounded text-capitalize shadow-sm"
					aria-labelledby="root-header-user-dropdown-toggle"
					data-testid="rootHeaderUserDropdownList">
					{user?.signed &&
					user?.status?.email_verified &&
					user?.position !== vars.app.positions.teamModerator ? (
						<li>
							<LocaleLink
								to="/user/me"
								className="dropdown-item text-capitalize"
								data-testid="rootHeaderUserDropdownDashboardNavigationLink">
								{t("actions.dashboard")}
							</LocaleLink>
						</li>
					) : (
						<></>
					)}
					<li>
						<button
							className="dropdown-item text-capitalize"
							type="button"
							onClick={onLogoutButtonClickHandler}
							data-testid="rootHeaderUserDropdownLogoutNavigationLink">
							{t("actions.logout")}
							{isLogoutUserLoadingState ? (
								<span className="spinner-border spinner-border-sm ms-auto" role="status">
									<span className="visually-hidden">{`${t("content.loading")}...`}</span>
								</span>
							) : (
								<></>
							)}
						</button>
					</li>
					<li>
						<a
							className="dropdown-item hstack gap-1"
							href={vars.app.externalSupportLink}
							data-testid="rootHeaderUserDropdownContactUsNavigationLink"
							target="_blank"
							rel="noreferrer noopener">
							{t("actions.report_an_issue")}
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="0.75rem"
								height="0.75rem"
								fill="currentColor"
								className="bi"
								viewBox="0 0 16 16">
								<path
									fillRule="evenodd"
									d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5"
								/>
								<path
									fillRule="evenodd"
									d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0z"
								/>
							</svg>
						</a>
					</li>
				</ul>
			</div>
		</div>
	);
};

export default HeaderUserDropdown;
